import React, { useEffect } from 'react'
import { Form } from 'antd';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import { api } from '../../../../../api/api';
import { ObjDB, DiscountObj } from '../../../../../types/apiTypes';
import FormStatus from '../../../../../components/form/FormStatus/FormStatus';
import { useFormStatus } from '../../../../../utils/hooks/useFormStatus';
import EditPlaceholder from '../../../../../components/common/EditPlaceholder/EditPlaceholder';
import { getAntField } from '../../../../../utils/form';
import { DiscountFormFields } from '../../../../../types/formFieldTypes';
import { errorNotification, invalidateQueries } from '../../../../../utils/rest';
import { SwitchDiscountItems } from '../../../../../types/propsTypes';
import { clearDiscountErrors, discountValidate, initialDiscountFields } from '../../formData/DiscountFormData';
import DiscountFormInputs from '../DiscountFormInputs/DiscountFormInputs';
import { queryKeysDiscount } from '../../../../../static/data/queryKeys';

type DiscountEditFormProps = {
    editItem: ObjDB<DiscountObj> | null
    switchData: SwitchDiscountItems
    setOpenedForm: React.Dispatch<React.SetStateAction<boolean>>
    setEditItem: React.Dispatch<React.SetStateAction<ObjDB<DiscountObj> | null>>
}

const DiscountEditForm: React.FC<React.PropsWithChildren<DiscountEditFormProps>> = React.memo((props) => {
    const {
        editItem, setOpenedForm, setEditItem,
        switchData,
    } = props

    const formStatus = useFormStatus()

    const {
        errors, values, touched,
        setFieldValue, handleChange,
        submitForm, setValues, setErrors,
    } = useFormik<DiscountFormFields>({
        initialValues: initialDiscountFields,
        onSubmit: (values) => {
            mutate(values)
        },
        validateOnChange: false,
        validate: discountValidate
    })

    const antField = getAntField<DiscountFormFields>(values, errors, touched)

    const {
        mutate
    } = useMutation(
        async (values: DiscountFormFields) => {
            if (editItem) return await api.discounts.edit(values, editItem)
        },
        {
            mutationKey: 'discount-edit',
            onSuccess() {
                invalidateQueries(queryKeysDiscount[2])
                formStatus.setMessage('Данные успешно изменены')
                formStatus.setStatus('success')
                setTimeout(clearEditItem, 1000)
            },
            onError: (err: Error) => errorNotification(err, 'Ошибка редактирования', formStatus),
            onMutate() {
                formStatus.setMessage('Отправка данных')
                formStatus.setStatus('fetching')
            }
        }
    )

    const clearForm = () => {
        setValues(initialDiscountFields)
        setErrors(clearDiscountErrors)
    }

    const clearEditItem = () => {
        clearForm()
        setOpenedForm(false)
        setTimeout(() => {
            setEditItem(null)
            formStatus.reset()
        }, 300)
    }

    const resetChanges = () => {
        if (editItem) {
            const { id, ...values } = editItem
            setValues(values)
            setErrors(clearDiscountErrors)
        }
    }

    useEffect(() => {
        if (editItem) return resetChanges()
        clearForm()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editItem])

    if (!editItem) return <EditPlaceholder />

    return (
        <Form
            fields={[
                antField('course_id', { withFalsy: true }),
                antField('city_id'),
                antField('name'),
                antField('discount'),
                antField('fix_price'),
                antField('type_discount'),
                antField('date_from', { notSetValue: true }),
                antField('date_to', { notSetValue: true }),
            ]}
        >
            {
                editItem &&
                <p className="uif-ant-form-title">
                    ID редактируемого объекта: <span>{editItem.id}</span>
                </p>
            }

            <DiscountFormInputs
                switchData={switchData}
                values={values}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                resetChanges={resetChanges}
                submitForm={submitForm}
            />

            <FormStatus formStatus={formStatus} />
        </Form>
    )
})

export default DiscountEditForm